import { useEffect } from 'react';
import { useStimulusStore } from '@/stores/useStimulusStore';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Volume2, Video, HelpCircle, Image as ImageIcon } from 'lucide-react';

interface Props {
  category?: string;
  className?: string;
}

export function StimulusCard({ category, className }: Props) {
  const { currentStimulus, fetchNextStimulus, loading } = useStimulusStore();

  useEffect(() => {
    if (!currentStimulus) {
      fetchNextStimulus(category);
    }
  }, [category]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentStimulus) {
    return null;
  }

  const renderContent = () => {
    switch (currentStimulus.contentType) {
      case 'text':
        return (
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary mt-1 shrink-0" />
            <p className="text-sm leading-relaxed">{currentStimulus.contentText}</p>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">Mensagem motivacional</span>
            </div>
            {currentStimulus.contentUrl && (
              <audio controls className="w-full">
                <source src={currentStimulus.contentUrl} type="audio/mpeg" />
                Seu navegador não suporta áudio.
              </audio>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">Vídeo inspirador</span>
            </div>
            {currentStimulus.contentUrl && (
              <video controls className="w-full rounded-lg">
                <source src={currentStimulus.contentUrl} type="video/mp4" />
                Seu navegador não suporta vídeo.
              </video>
            )}
          </div>
        );

      case 'quiz':
        try {
          const quizData = JSON.parse(currentStimulus.contentText || '{}');
          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">Reflexão rápida</span>
              </div>
              <p className="font-medium">{quizData.question}</p>
              <div className="space-y-2">
                {quizData.options?.map((option: string, i: number) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="w-full justify-start text-left"
                    size="sm"
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>
          );
        } catch {
          return <p className="text-sm text-muted-foreground">Erro ao carregar quiz</p>;
        }

      case 'metaphor':
        try {
          const metaphorData = JSON.parse(currentStimulus.contentText || '{}');
          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">Metáfora visual</span>
              </div>
              {metaphorData.image && (
                <img 
                  src={metaphorData.image} 
                  alt="Metáfora" 
                  className="w-full rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <p className="text-sm italic">{metaphorData.caption}</p>
            </div>
          );
        } catch {
          return <p className="text-sm">{currentStimulus.contentText}</p>;
        }

      case 'challenge':
        try {
          const challengeData = JSON.parse(currentStimulus.contentText || '{}');
          return (
            <div className="space-y-3">
              <div className="bg-primary/10 rounded-lg p-4">
                <h3 className="font-bold text-primary mb-2">{challengeData.title}</h3>
                <p className="text-sm">{challengeData.description}</p>
                {challengeData.points && (
                  <p className="text-xs text-muted-foreground mt-2">
                    +{challengeData.points} pontos
                  </p>
                )}
              </div>
              <Button className="w-full" size="sm">
                Aceitar desafio
              </Button>
            </div>
          );
        } catch {
          return <p className="text-sm text-muted-foreground">Erro ao carregar desafio</p>;
        }

      default:
        return <p className="text-sm">{currentStimulus.contentText}</p>;
    }
  };

  return (
    <Card className={className}>
      <CardContent className="p-6">
        {renderContent()}
      </CardContent>
    </Card>
  );
}
